import { useState } from "react";
import {
  uploadFile,
  approveMetadata,
  skipFile,
  submitFeedback,
  assertLlmConfigured,
  extractApiError,
} from "../services/api";
import { UploadResponse, ProcessingStatus } from "../types";

// Generated by GitHub Copilot
/**
 * Returns true when the API error indicates LLM configuration is missing/unavailable.
 */
const isLlmNotConfiguredError = (message: string): boolean =>
  /llm\s+not\s+configured|llm\s+unavailable/i.test(message);

export const usePipeline = () => {
  const [status, setStatus]       = useState<ProcessingStatus>("idle");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [result, setResult]       = useState<UploadResponse | null>(null);
  const [error, setError]         = useState<string | null>(null);

  const handleUpload = async (file: File) => {
    try {
      setStatus("uploading");
      setError(null);

      // Fail fast on the client: do not start upload when LLM is unavailable.
      await assertLlmConfigured();

      const data = await uploadFile(file);
      setSessionId(data.thread_id ?? data.session_id ?? null);
      setResult(data);
      setStatus("processing");
    } catch (err) {
      const message = extractApiError(err, "Upload failed. Please try again.");
      setError(message);
      if (isLlmNotConfiguredError(message)) {
        alert(message);
      }
      setStatus("error");
    }
  };

  const handleApprove = async () => {
    if (!sessionId) return;
    try {
      await approveMetadata(sessionId);
      setStatus("done");
    } catch (err) {
      setError("Approval failed. Please try again.");
      setStatus("error");
    }
  };

  const handleSkip = async () => {
    if (!sessionId) return;
    try {
      await skipFile(sessionId);
      reset();
    } catch (err) {
      setError("Skip failed. Please try again.");
      setStatus("error");
    }
  };

  const handleFeedback = async (feedback: string) => {
    if (!sessionId) return;
    try {
      await submitFeedback({ session_id: sessionId, feedback });
    } catch (err) {
      setError("Feedback submission failed.");
    }
  };

  const reset = () => {
    setStatus("idle");
    setSessionId(null);
    setResult(null);
    setError(null);
  };

  return {
    status,
    result,
    error,
    sessionId,
    handleUpload,
    handleApprove,
    handleSkip,
    handleFeedback,
    reset,
  };
};